import { Field, Select, Section, Callout, EstadoRow } from "../product_config/formkit";
import { CALCULATOR_TYPES, getCalculatorType } from "./calculator/calculatorTypes";
import { mergeOnTypeChange } from "./calculator/calculatorConfigSchema";
import { MINIFORMS, getMiniform, miniformSugeridoParaTipo } from "./calculator/miniforms";
import { frecuenciaPrimaDesdePrecios } from "../../pages/product_config/descripcionJsonRoundtrip";
import CargasModelConfig from "./calculator/CargasModelConfig";
import ValidacionExternaConfig from "./calculator/ValidacionExternaConfig";
import SimuladorSection from "./calculator/simulador/SimuladorSection";

export default function CalculatorConfigTab({ jsonConfig, handleJsonConfigChange, planId, productoId, preciosBase, hasChanges }) {
  const config = jsonConfig?.calculatorConfig || {};
  const typeDef = getCalculatorType(config.type) || CALCULATOR_TYPES[0];
  const TypeComponent = typeDef.Component;
  const miniformDef = getMiniform(config.miniform);
  const sugerido = miniformSugeridoParaTipo(config.type);

  // Frecuencia de la prima: NO se edita aquí. Sale del programa de cobro de cada
  // fila en "Precios Base" y se guarda sola en descripcion_json.frecuenciaPrima,
  // que es lo que el carrito de flow lee para decidir si la prima es una cuota
  // mensual o un pago único. Se muestra para que el admin vea qué quedó guardado.
  const frecuenciaDerivada = frecuenciaPrimaDesdePrecios(preciosBase);
  const frecuenciaGuardada = jsonConfig?.frecuenciaPrima;

  // onChange relativo a calculatorConfig
  const onChange = (relativePath, value) => handleJsonConfigChange(`calculatorConfig.${relativePath}`, value);
  const onTypeChange = (newType) => handleJsonConfigChange("calculatorConfig", mergeOnTypeChange(config, newType));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Selector de tipo */}
      <Field label="Tipo de cálculo" hint={typeDef.descripcion} htmlFor="calc-type" full>
        <Select
          id="calc-type"
          value={config.type || ""}
          placeholder="Seleccionar tipo"
          options={CALCULATOR_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          onChange={(e) => onTypeChange(e.target.value)}
        />
      </Field>

      {/* Cómo configurar — guía por tipo */}
      {Array.isArray(typeDef.comoConfigurar) && typeDef.comoConfigurar.length > 0 && (
        <Callout tone="info" title={`Cómo configurar «${typeDef.label}»`}>
          <ol style={{ margin: "4px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
            {typeDef.comoConfigurar.map((paso, i) => <li key={i}>{paso}</li>)}
          </ol>
        </Callout>
      )}

      {/* Frecuencia de la prima — derivada de Precios Base, no editable aquí */}
      {frecuenciaDerivada ? (
        <Callout tone="info" title={`Frecuencia de la prima: ${frecuenciaDerivada === "anual" ? "Anual (pago único)" : "Mensual (suscripción en cuotas)"}`}>
          Sale del programa de cobro que cargaste en «Precios Base (UF)» y se guarda sola con el plan.
          Es lo que el cotizador usa para mostrar «/año» o «/mes» y para no multiplicar por 12 un pago único.
          Para cambiarla, edita la frecuencia de cobro en «Precios Base (UF)».
        </Callout>
      ) : (
        <Callout tone="warn" title="No se puede determinar la frecuencia de la prima">
          {(preciosBase || []).filter((p) => p?.Activo ?? true).length === 0
            ? "Este plan no tiene precios activos. Carga al menos una fila en «Precios Base (UF)» con su frecuencia de cobro."
            : "Los precios activos del plan se contradicen: unos son anuales y otros mensuales. Déjalos todos en la misma frecuencia en «Precios Base (UF)»."}
          {frecuenciaGuardada
            ? ` Mientras tanto el cotizador sigue usando la última guardada: ${frecuenciaGuardada}.`
            : " Sin ella, el cotizador tratará la prima como mensual."}
        </Callout>
      )}

      {/* Aplica cargas + modelo (solo si el tipo soporta cargas) */}
      {typeDef.supportsCargas && (
        <Section title="Cargas familiares">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <EstadoRow
              titulo="Aplica cargas familiares"
              texto="El precio considera integrantes del grupo familiar."
              on={jsonConfig?.aplica_cargas !== false}
              onChange={(v) => handleJsonConfigChange("aplica_cargas", v)}
            />
            {jsonConfig?.aplica_cargas !== false && <CargasModelConfig config={config} onChange={onChange} />}
          </div>
        </Section>
      )}

      {/* Config específica del tipo */}
      <Section title={`Configuración: ${typeDef.label}`}>
        <TypeComponent config={config} onChange={onChange} />
      </Section>

      {/* Gate de RUT — aplica a cualquier tipo de cálculo: es del plan, no del tipo */}
      <Section
        title="RUT al cotizar"
        desc="Decide si el cotizador pide el RUT antes de mostrar precios, y contra qué sistemas se evalúa."
      >
        <ValidacionExternaConfig config={config} onChange={onChange} />
      </Section>

      {/* Miniform de flow asignado al plan */}
      <Section title="Miniform asignado" desc="El cotizador de flow renderiza este miniform para el plan.">
        <Field
          label="Miniform (flow)"
          hint={miniformDef
            ? miniformDef.descripcion
            : (sugerido ? `Sugerido para «${typeDef.label}»: ${sugerido.label}.` : "Selecciona el miniform que usará el cotizador en flow.")}
          htmlFor="calc-miniform"
          full
        >
          <Select
            id="calc-miniform"
            value={config.miniform || ""}
            options={[{ value: "", label: "Sin asignar" }, ...MINIFORMS.map((m) => ({ value: m.value, label: m.label }))]}
            onChange={(e) => onChange("miniform", e.target.value)}
          />
        </Field>
      </Section>

      {/* Simulador contra el microservicio calculator */}
      <Section title="Simulador" desc="Prueba la configuración guardada contra el microservicio calculator usando los inputs del miniform.">
        <SimuladorSection planId={planId} productoId={productoId} config={config} preciosBase={preciosBase} hasChanges={hasChanges} />
      </Section>
    </div>
  );
}
